// UX121 (docs/build-briefs/UX121-floor-menu-filters.md): the floor menu's Drinks, Bottles and Base filters.
// Pure, no DOM. It narrows whatever item list it is handed (the caller decides which items belong on the grid),
// so it stays separate from the list source and from the name search in menuSearch.ts.

import type { MenuItemDto } from "./api.ts";
import { CATEGORY_DISPLAY_ORDER, categoryLabel } from "./catalogDisplay.ts";

/** "all" shows everything; "drinks" shows cocktails; "bottles" shows every other tile. */
export type MenuKindFilter = "all" | "drinks" | "bottles";

export interface MenuFilters {
  kind: MenuKindFilter;
  /** A liquor category value, "" for All bases. */
  base: string;
}

export const NO_MENU_FILTERS: Readonly<MenuFilters> = { kind: "all", base: "" };

export interface BaseOption {
  value: string;
  label: string;
  count: number;
}

export function menuFiltersActive(filters: MenuFilters): boolean {
  return filters.kind !== "all" || filters.base !== "";
}

/** Drinks and Bottles are exclusive: tapping the lit one clears it, tapping the other switches. */
export function toggleKindFilter(filters: MenuFilters, tapped: "drinks" | "bottles"): MenuFilters {
  return { ...filters, kind: filters.kind === tapped ? "all" : tapped };
}

/** The liquor categories an item is made of: a drink's slots and recipe components, a bottle's own category. */
export function itemBaseCategories(item: MenuItemDto): string[] {
  if (item.kind === "cocktail" && "menuDrink" in item) {
    return [...new Set([...item.baseCategories, ...item.menuDrink.liquor_slots])];
  }
  if ("liquorProduct" in item) {
    return [item.liquorProduct.category];
  }
  return [];
}

function passesKind(item: MenuItemDto, kind: MenuKindFilter): boolean {
  if (kind === "all") return true;
  return kind === "drinks" ? item.kind === "cocktail" : item.kind !== "cocktail";
}

/** Items that pass the filters. With none on, the same items in the same order. */
export function applyMenuFilters<T extends MenuItemDto>(
  items: readonly T[],
  filters: MenuFilters,
): T[] {
  return items.filter(
    (item) =>
      passesKind(item, filters.kind) &&
      (filters.base === "" || itemBaseCategories(item).includes(filters.base)),
  );
}

/**
 * The Base list: every category some item carries, with how many items choosing it would show given the
 * Drinks or Bottles choice. Known spirits first in their fixed order, then any other category by label.
 * The chosen base always stays in the list so it can be seen and cleared.
 */
export function baseOptions(items: readonly MenuItemDto[], filters: MenuFilters): BaseOption[] {
  const counts = new Map<string, number>();
  for (const item of items) {
    if (!passesKind(item, filters.kind)) continue;
    for (const category of itemBaseCategories(item)) {
      counts.set(category, (counts.get(category) ?? 0) + 1);
    }
  }
  if (filters.base !== "" && !counts.has(filters.base)) {
    counts.set(filters.base, 0);
  }
  const rank = (value: string): number => {
    const at = CATEGORY_DISPLAY_ORDER.indexOf(value);
    return at === -1 ? CATEGORY_DISPLAY_ORDER.length : at;
  };
  return [...counts.entries()]
    .map(([value, count]) => ({ value, label: categoryLabel(value), count }))
    .sort((a, b) => rank(a.value) - rank(b.value) || a.label.localeCompare(b.label));
}

/** "Drinks", "Bottles" or "" for the result line. */
export function kindFilterLabel(kind: MenuKindFilter): string {
  return kind === "drinks" ? "Drinks" : kind === "bottles" ? "Bottles" : "";
}

/** "1 item" or "N items". */
export function itemCountLabel(count: number): string {
  return count === 1 ? "1 item" : `${count} items`;
}
